import React, { useEffect, useState } from 'react';
import {
  Ban,
  Building2,
  CheckCircle,
  Eye,
  History,
  Inbox,
  Link2,
  RefreshCw,
  Send,
  Users,
  XCircle
} from 'lucide-react';
import api, { extrairMensagemErro } from '../services/api';
import PerfilRapidoModal from '../components/PerfilRapidoModal';
import Paginacao, { MetadadosPaginacao, paginacaoVazia } from '../components/Paginacao';
import { dialog } from '../services/dialog';

type AbaVinculos = 'disponiveis' | 'recebidas' | 'enviadas' | 'ativos' | 'historico';

const statusClasse = (status?: string) => {
  if (status === 'ATIVO') return 'status-done';
  if (['RECUSADO', 'CANCELADO', 'DESATIVADO', 'BLOQUEADO'].includes(String(status))) return 'status-blocked';
  if (['PENDENTE', 'SUSPENSO'].includes(String(status))) return 'status-warning';
  return 'muted';
};

const dataCurta = (data?: string) => data ? new Intl.DateTimeFormat('pt-AO').format(new Date(data)) : 'Sem data';

const Fornecedores: React.FC = () => {
  const [aba, setAba] = useState<AbaVinculos>('disponiveis');
  const [disponiveis, setDisponiveis] = useState<any[]>([]);
  const [vinculos, setVinculos] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [perfilFornecedorId, setPerfilFornecedorId] = useState<string | null>(null);
  const [paginacao, setPaginacao] = useState<MetadadosPaginacao>(paginacaoVazia);

  const carregar = async (pagina = paginacao.pagina) => {
    try {
      setCarregando(true);
      setErro(null);
      const [fornecedoresResp, vinculosResp] = await Promise.all([
        api.get('/gerente/fornecedores/disponiveis', { params: { pagina, limite: paginacao.limite } }),
        api.get('/gerente/fornecedores/vinculos', { params: { pagina, limite: paginacao.limite, grupo: aba === 'disponiveis' ? undefined : aba } })
      ]);
      setDisponiveis(fornecedoresResp.data.dados || []);
      setVinculos(vinculosResp.data.dados || []);
      setPaginacao((aba === 'disponiveis' ? fornecedoresResp : vinculosResp).data.paginacao || paginacaoVazia);
    } catch (erroLista: any) {
      setErro(extrairMensagemErro(erroLista, 'Erro ao carregar vínculos com fornecedores.'));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aba]);

  const executar = async (acao: () => Promise<any>, fallback: string) => {
    try {
      setErro(null);
      setMensagem(null);
      const resposta = await acao();
      setMensagem(resposta.data?.mensagem || 'Vínculo atualizado.');
      await carregar();
    } catch (erroAcao: any) {
      setErro(extrairMensagemErro(erroAcao, fallback));
    }
  };

  const solicitar = (fornecedorId: string) =>
    executar(() => api.post('/gerente/fornecedores/vinculos/solicitar', { fornecedor_id: fornecedorId }), 'Erro ao solicitar vínculo.');

  const cancelar = (vinculoId: string) =>
    executar(() => api.post(`/gerente/fornecedores/vinculos/${vinculoId}/cancelar`), 'Erro ao cancelar solicitação.');

  const aceitar = (vinculoId: string) =>
    executar(() => api.post(`/gerente/fornecedores/vinculos/${vinculoId}/aceitar`), 'Erro ao aceitar solicitação.');

  const recusar = (vinculoId: string) =>
    executar(() => api.post(`/gerente/fornecedores/vinculos/${vinculoId}/recusar`), 'Erro ao recusar solicitação.');

  const desativar = async (vinculoId: string) => {
    if (!await dialog.confirm('Desativar vínculo com este fornecedor? Para voltar a operar será necessária nova solicitação.')) return;
    executar(() => api.post(`/gerente/fornecedores/vinculos/${vinculoId}/desativar`), 'Erro ao desativar vínculo.');
  };

  const recebidas = vinculos.filter((item) => item.status === 'PENDENTE' && item.solicitado_por === 'FORNECEDOR');
  const enviadas = vinculos.filter((item) => item.status === 'PENDENTE' && item.solicitado_por === 'TENANT');
  const ativos = vinculos.filter((item) => item.status === 'ATIVO');
  const historico = vinculos.filter((item) => !['PENDENTE', 'ATIVO'].includes(item.status));

  return (
    <div className="workspace-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Gerente da empresa</span>
          <h1>Fornecedores</h1>
          <p>Solicitações, vínculos ativos e histórico comercial do tenant.</p>
        </div>
        <button className="botao" type="button" onClick={() => carregar(paginacao.pagina)}>
          <RefreshCw size={18} aria-hidden="true" />
          Atualizar
        </button>
      </header>

      {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
      {erro && <div className="alerta alerta-erro">{erro}</div>}
      {carregando && <div className="inline-loading">A carregar fornecedores...</div>}

      <section className="panel">
        <div className="admin-tabs">
          {[
            ['disponiveis', 'Disponíveis', Building2],
            ['recebidas', 'Recebidas', Inbox],
            ['enviadas', 'Enviadas', Send],
            ['ativos', 'Vinculados', Link2],
            ['historico', 'Histórico', History]
          ].map(([id, label, Icone]) => (
            <button key={id as string} type="button" className={aba === id ? 'active' : ''} onClick={() => setAba(id as AbaVinculos)}>
              {React.createElement(Icone as any, { size: 16, 'aria-hidden': true })}
              {label as string}
            </button>
          ))}
        </div>

        <div className="table-shell">
          {aba === 'disponiveis' && (
            <table>
              <thead><tr><th>Fornecedor</th><th>NIF</th><th>Status</th><th>Ações</th></tr></thead>
              <tbody>
                {disponiveis.map((fornecedor) => (
                  <tr key={fornecedor.id}>
                    <td>{fornecedor.nome_empresa}<small>{fornecedor.email}</small></td>
                    <td>{fornecedor.nif || 'sem NIF'}</td>
                    <td><span className={`status-pill ${statusClasse(fornecedor.status)}`}>{fornecedor.status}</span></td>
                    <td><button className="botao botao-primario" type="button" onClick={() => solicitar(fornecedor.id)}><Send size={16} aria-hidden="true" />Solicitar</button></td>
                  </tr>
                ))}
                {disponiveis.length === 0 && <tr><td colSpan={4}>Sem fornecedores disponíveis.</td></tr>}
              </tbody>
            </table>
          )}

          {aba === 'recebidas' && (
            <table>
              <thead><tr><th>Fornecedor</th><th>Solicitado em</th><th>Status</th><th>Ações</th></tr></thead>
              <tbody>
                {recebidas.map((vinculo) => (
                  <tr key={vinculo.id}>
                    <td>{vinculo.fornecedor?.nome_empresa}<small>{vinculo.fornecedor?.email}</small></td>
                    <td>{dataCurta(vinculo.data_solicitacao)}</td>
                    <td><span className="status-pill status-warning">PENDENTE</span></td>
                    <td><div className="inline-actions"><button className="botao botao-primario" type="button" onClick={() => aceitar(vinculo.id)}><CheckCircle size={16} aria-hidden="true" />Aceitar</button><button className="botao botao-secundario" type="button" onClick={() => recusar(vinculo.id)}><XCircle size={16} aria-hidden="true" />Recusar</button></div></td>
                  </tr>
                ))}
                {recebidas.length === 0 && <tr><td colSpan={4}>Sem solicitações recebidas.</td></tr>}
              </tbody>
            </table>
          )}

          {aba === 'enviadas' && (
            <table>
              <thead><tr><th>Fornecedor</th><th>Solicitado em</th><th>Status</th><th>Ações</th></tr></thead>
              <tbody>
                {enviadas.map((vinculo) => (
                  <tr key={vinculo.id}>
                    <td>{vinculo.fornecedor?.nome_empresa}<small>{vinculo.fornecedor?.email}</small></td>
                    <td>{dataCurta(vinculo.data_solicitacao)}</td>
                    <td><span className="status-pill status-warning">PENDENTE</span></td>
                    <td><button className="botao botao-secundario" type="button" onClick={() => cancelar(vinculo.id)}><XCircle size={16} aria-hidden="true" />Cancelar</button></td>
                  </tr>
                ))}
                {enviadas.length === 0 && <tr><td colSpan={4}>Sem solicitações enviadas.</td></tr>}
              </tbody>
            </table>
          )}

          {aba === 'ativos' && (
            <table>
              <thead><tr><th>Fornecedor</th><th>Ativo desde</th><th>Status</th><th>Ações</th></tr></thead>
              <tbody>
                {ativos.map((vinculo) => (
                  <tr key={vinculo.id}>
                    <td>{vinculo.fornecedor?.nome_empresa}<small>{vinculo.fornecedor?.email}</small></td>
                    <td>{dataCurta(vinculo.data_resposta || vinculo.data_atualizacao)}</td>
                    <td><span className="status-pill status-done">ATIVO</span></td>
                    <td><div className="inline-actions"><button className="botao" type="button" onClick={() => setPerfilFornecedorId(vinculo.fornecedor_id)}><Eye size={16} aria-hidden="true" />Ver perfil</button><button className="botao botao-secundario" type="button" onClick={() => desativar(vinculo.id)}><Ban size={16} aria-hidden="true" />Desativar</button></div></td>
                  </tr>
                ))}
                {ativos.length === 0 && <tr><td colSpan={4}>Sem fornecedores vinculados.</td></tr>}
              </tbody>
            </table>
          )}

          {aba === 'historico' && (
            <table>
              <thead><tr><th>Fornecedor</th><th>Status</th><th>Atualizado em</th><th>Motivo</th></tr></thead>
              <tbody>
                {historico.map((vinculo) => (
                  <tr key={vinculo.id}>
                    <td><span className="cell-with-icon"><Users size={16} aria-hidden="true" />{vinculo.fornecedor?.nome_empresa}</span><small>{vinculo.fornecedor?.email}</small></td>
                    <td><span className={`status-pill ${statusClasse(vinculo.status)}`}>{vinculo.status}</span></td>
                    <td>{dataCurta(vinculo.data_atualizacao)}</td>
                    <td>{vinculo.motivo_recusa || vinculo.motivo_desativacao || 'Sem motivo registado.'}</td>
                  </tr>
                ))}
                {historico.length === 0 && <tr><td colSpan={4}>Sem histórico.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
        {!carregando && <Paginacao paginacao={paginacao} carregando={carregando} aoMudarPagina={carregar} />}
      </section>
      <PerfilRapidoModal
        tipo="fornecedor"
        id={perfilFornecedorId}
        aberto={Boolean(perfilFornecedorId)}
        onClose={() => setPerfilFornecedorId(null)}
      />
    </div>
  );
};

export default Fornecedores;
